import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { CapsuleTrend } from '@/components';
import { NCCardSectionHeader, printLatestDate, printNthYearDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { isNullish } from '@/utils/booleans';

export const NDVIStatsTile = ({
  latestAnalysisFact,
  oneYearUpliftFact,
  benchmarks,
  trendFact,
}: {
  latestAnalysisFact: PlotReportFactElement<unknown> | undefined;
  oneYearUpliftFact: PlotReportFactElement<unknown> | undefined;
  trendFact: PlotReportFactElement<unknown> | undefined;
  benchmarks: number[];
}) => {
  const { t } = useTranslation();

  const latestAnalysisDate = printLatestDate([latestAnalysisFact?.measured_at]);
  const latestAnalysisTranslation = t('shared.ncaDetail.details.labels.latestAnalysis');

  const oneYearUpliftMeasuredAtDate = printNthYearDate([oneYearUpliftFact?.measured_at], -1);

  const benchmark = useBenchmarkForFact(benchmarks, latestAnalysisFact?.value as string | undefined);
  const benchmarkLabel = benchmark ? t(`global.benchmark.${benchmark}`).toLowerCase() : undefined;

  let ndviTrendDisplay = `${useDisplayNumber(Number(trendFact?.value ?? 0), {
    signDisplay: 'always',
  })} %`;
  ndviTrendDisplay = !isNullish(trendFact?.value) ? ndviTrendDisplay : '-';

  return (
    <Stack className='gap-8 px-6 py-8 lg:gap-4' data-testid='ndvi-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.ndvi.stats.header.title')}
          supplementaryInfo={`${latestAnalysisTranslation} ${latestAnalysisDate}`}
          popover={{
            title: t('shared.ncaDetail.details.ndvi.stats.header.title'),
            body: t('shared.ncaDetail.details.ndvi.stats.header.tooltipBody'),
          }}
        />
        <Stack direction='row' className='flex-wrap gap-16'>
          {latestAnalysisFact && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.labels.latestAnalysis')}
              fact={latestAnalysisFact}
              capsuleIconProps={{
                icon: getNCChangeIcon(latestAnalysisFact),
                variant: getNCChangeIconVariant(latestAnalysisFact),
              }}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
                body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', {
                  date: latestAnalysisDate,
                }),
                popoverTriggerProps: {
                  'data-testid': 'ndvi-latest-analysis-popover',
                },
              }}
              trend={
                typeof trendFact?.value === 'number' && (
                  <CapsuleTrend variant={trendFact.value < 0 ? 'negative' : 'positive'}>
                    {ndviTrendDisplay}
                  </CapsuleTrend>
                )
              }
            />
          )}
          {oneYearUpliftFact && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.labels.oneYearChange')}
              capsuleIconProps={{
                icon: getNCChangeIcon(oneYearUpliftFact),
                variant: getNCChangeIconVariant(oneYearUpliftFact),
              }}
              fact={oneYearUpliftFact}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.ndvi.explainers.oneYearChange.title'),
                body: t('shared.ncaDetail.details.ndvi.explainers.oneYearChange.body', {
                  date: latestAnalysisDate,
                  previousDate: oneYearUpliftMeasuredAtDate,
                }),
              }}
            />
          )}
        </Stack>
        <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
          {benchmark && <BenchmarkPill benchmarkType={benchmark} />}
          <span className='typography-body1Bold'>{t('shared.ncaDetail.details.ndvi.stats.explainer.title')}</span>
          <p className='typography-body1 text-text-secondary'>
            {benchmarkLabel &&
              t('shared.ncaDetail.details.ndvi.stats.explainer.body1', {
                benchmark: benchmarkLabel,
              })}
            {t('shared.ncaDetail.details.ndvi.stats.explainer.body2')}
          </p>
        </Stack>
      </div>
    </Stack>
  );
};
